import React, { useCallback, useEffect, useState } from 'react';
import {
  Box, Button, Checkbox, Chip, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, FormControlLabel,
  MenuItem, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TextField, Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import { useAuth } from '../../context/AuthContext';
import { useToast } from '../../context/ToastContext';
import { AuthService } from '../../services/auth.service';
import { ChequeLayoutService } from '../../services/cheque.service';
import { helpElement } from '../../help/attrs';
import { PERMISSIONS } from '../../constants/permissions';
import { ChequeLayout } from '../../interfaces/bank/cheque.interface';

type NumKey = 'pageWidthMm' | 'pageHeightMm' | 'dateXMm' | 'dateYMm' | 'payeeXMm' | 'payeeYMm' | 'amountXMm'
  | 'amountYMm' | 'wordsXMm' | 'wordsYMm' | 'wordsMaxWidthMm' | 'fontSize';
const NUM_FIELDS: { key: NumKey; label: string }[] = [
  { key: 'pageWidthMm', label: 'Page width (mm)' }, { key: 'pageHeightMm', label: 'Page height (mm)' },
  { key: 'dateXMm', label: 'Date X (mm)' }, { key: 'dateYMm', label: 'Date Y (mm)' },
  { key: 'payeeXMm', label: 'Payee X (mm)' }, { key: 'payeeYMm', label: 'Payee Y (mm)' },
  { key: 'amountXMm', label: 'Amount X (mm)' }, { key: 'amountYMm', label: 'Amount Y (mm)' },
  { key: 'wordsXMm', label: 'Words X (mm)' }, { key: 'wordsYMm', label: 'Words Y (mm)' },
  { key: 'wordsMaxWidthMm', label: 'Words max width (mm)' }, { key: 'fontSize', label: 'Font size (pt)' },
];

/** Help keys of the numeric layout fields (literals so the help key scan sees them). */
const LAYOUT_NUM_HELP: Record<NumKey, { helpKey: string }> = {
  pageWidthMm: { helpKey: 'bank.chequeLayout.field.pageWidth' }, pageHeightMm: { helpKey: 'bank.chequeLayout.field.pageHeight' },
  dateXMm: { helpKey: 'bank.chequeLayout.field.dateX' }, dateYMm: { helpKey: 'bank.chequeLayout.field.dateY' },
  payeeXMm: { helpKey: 'bank.chequeLayout.field.payeeX' }, payeeYMm: { helpKey: 'bank.chequeLayout.field.payeeY' },
  amountXMm: { helpKey: 'bank.chequeLayout.field.amountX' }, amountYMm: { helpKey: 'bank.chequeLayout.field.amountY' },
  wordsXMm: { helpKey: 'bank.chequeLayout.field.wordsX' }, wordsYMm: { helpKey: 'bank.chequeLayout.field.wordsY' },
  wordsMaxWidthMm: { helpKey: 'bank.chequeLayout.field.wordsWidth' }, fontSize: { helpKey: 'bank.chequeLayout.field.fontSize' },
};

const blank = (institutionId: string): ChequeLayout => ({
  institutionId, layoutName: '', bankName: '', pageWidthMm: 203, pageHeightMm: 89, dateXMm: 150, dateYMm: 12,
  payeeXMm: 25, payeeYMm: 28, amountXMm: 155, amountYMm: 42, wordsXMm: 30, wordsYMm: 38, wordsMaxWidthMm: 115,
  crossing: true, fontSize: 10, wordsLanguage: 'EN', defaultLayout: false,
});

const ChequeLayouts: React.FC = () => {
  const toast = useToast();
  const { hasPermission } = useAuth();
  const canManage = hasPermission(PERMISSIONS.BANK_ACCOUNT_MANAGE);
  const institutionId = AuthService.getSelectedInstitution()!;
  const [layouts, setLayouts] = useState<ChequeLayout[]>([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState<ChequeLayout | null>(null);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      setLayouts(await ChequeLayoutService.list(institutionId));
    } catch (e) {
      toast.showApiError(e, 'Failed to load layouts');
    } finally {
      setLoading(false);
    }
  }, [institutionId]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { load(); }, [load]);

  const loadDefaults = async () => {
    try {
      await ChequeLayoutService.loadDefaults(institutionId);
      toast.showSuccess('Default layouts loaded');
      await load();
    } catch (e) {
      toast.showApiError(e, 'Failed to load default layouts');
    }
  };

  const save = async () => {
    if (!editing) return;
    try {
      setSaving(true);
      if (editing.id) await ChequeLayoutService.update(editing.id, editing);
      else await ChequeLayoutService.create(editing);
      toast.showSuccess('Layout saved');
      setEditing(null);
      await load();
    } catch (e) {
      toast.showApiError(e, 'Failed to save layout');
    } finally {
      setSaving(false);
    }
  };

  const remove = async (l: ChequeLayout) => {
    try {
      await ChequeLayoutService.remove(l.id!);
      toast.showSuccess('Layout removed');
      await load();
    } catch (e) {
      toast.showApiError(e, 'Failed to remove layout');
    }
  };

  return (
    <Box data-help-page="banking.chequeLayouts.page" sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, flexWrap: 'wrap', gap: 1 }}>
        <Typography variant="h5">Cheque print layouts</Typography>
        {canManage && (
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
            <Button data-help="banking.chequeLayouts.loadDefaults" variant="outlined" onClick={loadDefaults}>Load default layouts</Button>
            <Button data-help="banking.chequeLayouts.new" variant="contained" startIcon={<AddIcon />} onClick={() => setEditing(blank(institutionId))}>New layout</Button>
          </Box>
        )}
      </Box>
      <Typography variant="body2" sx={{ mb: 2 }}>
        Positions are millimetres from the top-left of the cheque leaf. Print a test on plain paper over a real cheque
        and adjust until the fields line up.
      </Typography>
      {loading ? <CircularProgress /> : (
        <TableContainer data-help-section="banking.chequeLayouts.table" component={Paper}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell data-help="banking.chequeLayouts.col.layout">Layout</TableCell><TableCell data-help="banking.chequeLayouts.col.bank">Bank</TableCell><TableCell data-help="banking.chequeLayouts.col.size">Size (mm)</TableCell>
                <TableCell data-help="banking.chequeLayouts.col.words">Words</TableCell><TableCell data-help="banking.chequeLayouts.col.crossing">Crossing</TableCell><TableCell data-help="banking.chequeLayouts.col.actions" />
              </TableRow>
            </TableHead>
            <TableBody>
              {layouts.map(l => (
                <TableRow key={l.id} hover>
                  <TableCell>{l.layoutName} {l.defaultLayout && <Chip size="small" label="Default" sx={{ ml: 1 }} />}</TableCell>
                  <TableCell>{l.bankName ?? 'Any'}</TableCell>
                  <TableCell>{l.pageWidthMm} x {l.pageHeightMm}</TableCell>
                  <TableCell>{l.wordsLanguage === 'BN' ? 'Bangla' : 'English'}</TableCell>
                  <TableCell>{l.crossing ? 'A/C payee' : 'No'}</TableCell>
                  <TableCell>
                    {canManage && (
                      <>
                        <Button data-help="banking.chequeLayouts.action.edit" size="small" onClick={() => setEditing({ ...l })}>Edit</Button>
                        <Button data-help="banking.chequeLayouts.action.remove" size="small" color="error" onClick={() => remove(l)}>Remove</Button>
                      </>
                    )}
                  </TableCell>
                </TableRow>
              ))}
              {layouts.length === 0 && (
                <TableRow><TableCell colSpan={6} align="center">No layouts. Use "Load default layouts" to start.</TableCell></TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}
      <Dialog open={!!editing} onClose={() => setEditing(null)} maxWidth="md" fullWidth PaperProps={{ 'data-help-page': 'bank.chequeLayout.page' } as any}>
        <DialogTitle>{editing?.id ? 'Edit layout' : 'New layout'}</DialogTitle>
        {editing && (
          <DialogContent sx={{ pt: '8px !important' }}>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }} data-help-section="bank.chequeLayout.section.layout">
              <TextField data-help="bank.chequeLayout.field.name" label="Layout name" value={editing.layoutName} required sx={{ flex: '1 1 260px', maxWidth: 360 }}
                onChange={e => setEditing({ ...editing, layoutName: e.target.value })} />
              <TextField data-help="bank.chequeLayout.field.bankName" label="Bank name (optional)" value={editing.bankName ?? ''} sx={{ flex: '1 1 220px', maxWidth: 320 }}
                onChange={e => setEditing({ ...editing, bankName: e.target.value })} />
              <TextField data-help="bank.chequeLayout.field.words" select label="Amount in words" value={editing.wordsLanguage} sx={{ flex: '1 1 160px', maxWidth: 220 }}
                onChange={e => setEditing({ ...editing, wordsLanguage: e.target.value as 'EN' | 'BN' })}>
                <MenuItem value="EN">English</MenuItem>
                <MenuItem value="BN">Bangla</MenuItem>
              </TextField>
              {NUM_FIELDS.map(f => (
                <TextField key={f.key} {...helpElement(LAYOUT_NUM_HELP[f.key].helpKey)} label={f.label} type="number" value={editing[f.key]} sx={{ flex: '1 1 150px', maxWidth: 200 }}
                  onChange={e => setEditing({ ...editing, [f.key]: Number(e.target.value) })} />
              ))}
              <FormControlLabel data-help="bank.chequeLayout.field.crossing" control={<Checkbox checked={editing.crossing}
                onChange={e => setEditing({ ...editing, crossing: e.target.checked })} />} label="Print A/C payee crossing" />
              <FormControlLabel data-help="bank.chequeLayout.field.default" control={<Checkbox checked={editing.defaultLayout}
                onChange={e => setEditing({ ...editing, defaultLayout: e.target.checked })} />} label="Default layout" />
            </Box>
          </DialogContent>
        )}
        <DialogActions>
          <Button data-help="bank.chequeLayout.cancel" onClick={() => setEditing(null)}>Cancel</Button>
          <Button data-help="bank.chequeLayout.save" variant="contained" disabled={saving || !editing?.layoutName.trim()} onClick={save}>Save</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ChequeLayouts;
